import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft,
  AlertTriangle,
  Shield,
  Stethoscope,
  ChevronRight,
  ChevronDown,
  Phone,
  ShoppingBag,
  HelpCircle,
  Link2
} from 'lucide-react';

const pathologiesData = {
  diabete: {
    id: 'diabete',
    name: 'Diabète',
    icon: '🩸',
    color: 'from-blue-500 to-indigo-600',
    bgColor: 'bg-blue-50',
    description: 'Le diabète est une maladie chronique caractérisée par un excès de sucre dans le sang (hyperglycémie). Il existe principalement deux types : le diabète de type 1 (insulino-dépendant) et le diabète de type 2 (le plus fréquent, souvent lié au mode de vie).',
    symptoms: [
      'Soif excessive et bouche sèche',
      'Envie fréquente d\'uriner',
      'Fatigue inhabituelle',
      'Perte de poids inexpliquée',
      'Vision floue',
      'Cicatrisation lente des plaies',
      'Infections fréquentes'
    ],
    prevention: [
      'Maintenir un poids santé',
      'Pratiquer une activité physique régulière (30 min/jour)',
      'Adopter une alimentation équilibrée, riche en fibres',
      'Limiter les sucres rapides et les graisses saturées',
      'Éviter le tabac et l\'alcool excessif',
      'Faire contrôler sa glycémie régulièrement après 40 ans'
    ],
    treatment: [
      'Suivi médical régulier avec contrôle de la glycémie',
      'Médicaments antidiabétiques oraux (selon prescription)',
      'Injections d\'insuline si nécessaire',
      'Régime alimentaire adapté',
      'Activité physique régulière',
      'Surveillance des pieds et des yeux'
    ],
    products: ['Glucomètres', 'Bandelettes de test', 'Compléments alimentaires', 'Soins des pieds'],
    faq: [
      { question: 'Quels sont les premiers signes du diabète ?', answer: 'Les premiers signes incluent une soif excessive, des envies fréquentes d\'uriner, une fatigue inhabituelle, une perte de poids inexpliquée et une vision floue. Consultez un médecin si vous présentez ces symptômes.' },
      { question: 'Comment prévenir le diabète de type 2 ?', answer: 'La prévention passe par une alimentation équilibrée, une activité physique régulière (30 min/jour), le maintien d\'un poids santé et la limitation des sucres et graisses saturées.' },
      { question: 'Quand faut-il consulter un médecin ?', answer: 'Consultez immédiatement si vous avez une soif intense, urinez très fréquemment, perdez du poids sans raison, ou si votre glycémie est supérieure à 1,26 g/L à jeun.' },
      { question: 'Comment contrôler sa glycémie au quotidien ?', answer: 'Utilisez un glucomètre pour mesurer votre glycémie. Tenez un carnet de suivi, respectez votre traitement et adoptez une alimentation adaptée. Notre pharmacie propose des glucomètres et bandelettes.' }
    ],
    relatedPages: ['hta', 'fatigue', 'carence-vitamines']
  },
  hta: {
    id: 'hta',
    name: 'Hypertension (HTA)',
    icon: '❤️',
    color: 'from-red-500 to-rose-600',
    bgColor: 'bg-red-50',
    description: 'L\'hypertension artérielle (HTA) est une élévation anormale de la pression du sang dans les artères. Appelée "tueur silencieux", elle peut passer inaperçue pendant des années tout en endommageant le cœur, les reins et le cerveau.',
    symptoms: [
      'Souvent aucun symptôme (maladie silencieuse)',
      'Maux de tête matinaux',
      'Vertiges',
      'Troubles de la vision',
      'Bourdonnements d\'oreilles',
      'Palpitations',
      'Saignements de nez fréquents'
    ],
    prevention: [
      'Réduire la consommation de sel (< 5g/jour)',
      'Manger plus de fruits et légumes',
      'Limiter l\'alcool',
      'Arrêter le tabac',
      'Pratiquer une activité physique régulière',
      'Gérer le stress',
      'Maintenir un poids santé',
      'Faire contrôler sa tension régulièrement'
    ],
    treatment: [
      'Mesure régulière de la tension artérielle',
      'Médicaments antihypertenseurs (selon prescription)',
      'Modification du mode de vie',
      'Régime pauvre en sel (régime DASH)',
      'Suivi médical régulier',
      'Gestion du stress'
    ],
    products: ['Tensiomètres', 'Sel de régime', 'Compléments (oméga-3, magnésium)'],
    faq: [
      { question: 'Quelle est la tension artérielle normale ?', answer: 'Une tension normale est inférieure à 140/90 mmHg. L\'idéal est autour de 120/80 mmHg. Au-delà de 140/90, on parle d\'hypertension.' },
      { question: 'L\'hypertension peut-elle être guérie ?', answer: 'L\'hypertension ne se guérit généralement pas, mais elle se contrôle très bien avec un traitement adapté et une bonne hygiène de vie.' },
      { question: 'Quels aliments éviter en cas d\'hypertension ?', answer: 'Limitez le sel, les plats préparés, la charcuterie, les fromages salés et l\'alcool. Privilégiez les fruits, légumes et aliments riches en potassium.' },
      { question: 'Comment mesurer sa tension à domicile ?', answer: 'Utilisez un tensiomètre validé, mesurez au calme après 5 minutes de repos, en position assise. Prenez 3 mesures matin et soir pendant 3 jours.' }
    ],
    relatedPages: ['diabete', 'fatigue', 'migraines']
  },
  palu: {
    id: 'palu',
    name: 'Paludisme (Malaria)',
    icon: '🦟',
    color: 'from-green-500 to-emerald-600',
    bgColor: 'bg-green-50',
    description: 'Le paludisme est une maladie infectieuse potentiellement mortelle, transmise par la piqûre de moustiques Anophèles infectés. Très répandu en Afrique, il nécessite une prise en charge rapide.',
    symptoms: [
      'Fièvre élevée avec frissons',
      'Maux de tête intenses',
      'Douleurs musculaires et articulaires',
      'Fatigue extrême',
      'Nausées et vomissements',
      'Transpiration abondante',
      'Anémie'
    ],
    prevention: [
      'Dormir sous une moustiquaire imprégnée d\'insecticide',
      'Utiliser des répulsifs anti-moustiques',
      'Porter des vêtements longs le soir',
      'Installer des moustiquaires aux fenêtres',
      'Éliminer les eaux stagnantes autour de la maison',
      'Prendre un traitement préventif si conseillé',
      'Utiliser des diffuseurs ou spirales anti-moustiques'
    ],
    treatment: [
      'Consultation médicale urgente dès les premiers symptômes',
      'Test de diagnostic rapide (TDR) ou goutte épaisse',
      'Traitement antipaludéen (ACT) selon prescription',
      'Repos et hydratation',
      'Traitement de la fièvre',
      'Ne jamais s\'automédiquer'
    ],
    products: ['Moustiquaires imprégnées', 'Répulsifs anti-moustiques', 'Sprays insecticides', 'Diffuseurs électriques'],
    faq: [
      { question: 'Comment savoir si j\'ai le paludisme ?', answer: 'Les symptômes typiques sont une fièvre élevée avec frissons, maux de tête, courbatures et fatigue. Seul un test (TDR ou goutte épaisse) peut confirmer le diagnostic.' },
      { question: 'Le paludisme est-il contagieux ?', answer: 'Non, le paludisme ne se transmet pas de personne à personne. Il est transmis uniquement par la piqûre d\'un moustique Anophèle infecté.' },
      { question: 'Peut-on prévenir le paludisme ?', answer: 'Oui ! Dormez sous moustiquaire imprégnée, utilisez des répulsifs, portez des vêtements longs le soir et éliminez les eaux stagnantes.' },
      { question: 'Que faire en cas de suspicion de paludisme ?', answer: 'Consultez immédiatement un médecin ou rendez-vous dans un centre de santé. Ne vous auto-médiquez pas. Un traitement rapide est essentiel.' }
    ],
    relatedPages: ['grippe', 'fatigue']
  },
  grippe: {
    id: 'grippe',
    name: 'États grippaux',
    icon: '🤒',
    color: 'from-amber-500 to-orange-600',
    bgColor: 'bg-amber-50',
    description: 'Les états grippaux regroupent la grippe et les infections respiratoires similaires causées par des virus. Ils sont très contagieux et se propagent facilement, surtout en saison des pluies.',
    symptoms: [
      'Fièvre soudaine (38-40°C)',
      'Frissons et courbatures',
      'Maux de tête',
      'Fatigue intense',
      'Toux sèche',
      'Mal de gorge',
      'Nez bouché ou qui coule',
      'Douleurs musculaires'
    ],
    prevention: [
      'Se laver les mains fréquemment',
      'Éviter les contacts avec les personnes malades',
      'Tousser ou éternuer dans le coude',
      'Aérer régulièrement les pièces',
      'Renforcer son système immunitaire (vitamine C, zinc)',
      'Éviter de se toucher le visage',
      'Utiliser des mouchoirs à usage unique'
    ],
    treatment: [
      'Repos au lit',
      'Boire beaucoup de liquides (eau, tisanes, bouillons)',
      'Paracétamol pour la fièvre et les douleurs',
      'Sirops contre la toux si nécessaire',
      'Décongestionnants nasaux',
      'Pastilles pour la gorge',
      'Consulter si les symptômes persistent plus de 5 jours'
    ],
    products: ['Paracétamol', 'Vitamine C', 'Sirops contre la toux', 'Pastilles pour la gorge', 'Sprays nasaux'],
    faq: [
      { question: 'Quelle est la différence entre grippe et rhume ?', answer: 'La grippe provoque une fièvre élevée soudaine, des courbatures intenses et une grande fatigue. Le rhume est plus léger avec surtout un nez qui coule et des éternuements.' },
      { question: 'Combien de temps dure une grippe ?', answer: 'La grippe dure généralement 5 à 7 jours. La fatigue peut persister 2 à 3 semaines. Consultez si les symptômes s\'aggravent ou persistent.' },
      { question: 'Quand faut-il consulter un médecin ?', answer: 'Consultez si la fièvre dépasse 40°C, si vous avez des difficultés respiratoires, si les symptômes persistent plus de 5 jours, ou si vous êtes à risque (personnes âgées, femmes enceintes).' },
      { question: 'Comment renforcer son système immunitaire ?', answer: 'Adoptez une alimentation riche en fruits et légumes, prenez de la vitamine C et du zinc, dormez suffisamment et pratiquez une activité physique régulière.' }
    ],
    relatedPages: ['palu', 'fatigue', 'carence-vitamines']
  },
  digestion: {
    id: 'digestion',
    name: 'Troubles digestifs',
    icon: '🫃',
    color: 'from-orange-500 to-amber-600',
    bgColor: 'bg-orange-50',
    description: 'Les troubles digestifs comprennent les brûlures d\'estomac, les ballonnements, les nausées, la constipation et les diarrhées. Ils sont souvent liés à l\'alimentation ou au stress.',
    symptoms: [
      'Brûlures d\'estomac et reflux acide',
      'Ballonnements et gaz',
      'Nausées et vomissements',
      'Douleurs abdominales',
      'Constipation ou diarrhée',
      'Sensation de lourdeur après les repas',
      'Perte d\'appétit'
    ],
    prevention: [
      'Manger lentement et bien mastiquer',
      'Éviter les repas trop copieux',
      'Limiter les aliments gras, épicés et acides',
      'Boire suffisamment d\'eau',
      'Manger des fibres (fruits, légumes, céréales complètes)',
      'Éviter de manger tard le soir',
      'Gérer le stress',
      'Limiter alcool et tabac'
    ],
    treatment: [
      'Antiacides pour les brûlures d\'estomac',
      'Charbon actif pour les ballonnements',
      'Anti-diarrhéiques ou laxatifs selon les cas',
      'Probiotiques pour rééquilibrer la flore intestinale',
      'Tisanes digestives (menthe, gingembre)',
      'Consulter si les symptômes persistent'
    ],
    products: ['Antiacides', 'Charbon actif', 'Probiotiques', 'Tisanes digestives', 'Laxatifs doux'],
    faq: [
      { question: 'Comment soulager rapidement les brûlures d\'estomac ?', answer: 'Prenez un antiacide, évitez de vous allonger après le repas, et buvez un peu d\'eau. Évitez les aliments acides, épicés et le café.' },
      { question: 'Les ballonnements sont-ils graves ?', answer: 'Généralement non, ils sont souvent liés à l\'alimentation ou au stress. Cependant, consultez si ils sont persistants, douloureux ou accompagnés d\'autres symptômes.' },
      { question: 'Quand prendre des probiotiques ?', answer: 'Les probiotiques sont utiles après une gastro-entérite, un traitement antibiotique, ou en cas de troubles digestifs chroniques. Demandez conseil à votre pharmacien.' },
      { question: 'Quels aliments favorisent une bonne digestion ?', answer: 'Privilégiez les fibres (fruits, légumes, céréales complètes), les yaourts, le gingembre et la menthe. Évitez les aliments gras, épicés et les boissons gazeuses.' }
    ],
    relatedPages: ['fatigue', 'carence-vitamines']
  },
  acne: {
    id: 'acne',
    name: 'Acné & Peau grasse',
    icon: '✨',
    color: 'from-pink-500 to-rose-600',
    bgColor: 'bg-pink-50',
    description: 'L\'acné est une maladie de la peau caractérisée par des boutons, points noirs et microkystes. Fréquente chez les adolescents, elle peut aussi toucher les adultes, surtout sur les peaux noires et métissées.',
    symptoms: [
      'Points noirs (comédons ouverts)',
      'Points blancs (comédons fermés)',
      'Boutons rouges inflammatoires',
      'Pustules (boutons avec pus)',
      'Peau brillante et grasse',
      'Pores dilatés',
      'Taches pigmentaires (sur peaux noires)'
    ],
    prevention: [
      'Nettoyer la peau matin et soir avec un produit doux',
      'Ne pas toucher ou percer les boutons',
      'Utiliser des produits non comédogènes',
      'Hydrater même les peaux grasses',
      'Protéger la peau du soleil',
      'Limiter les aliments sucrés et gras',
      'Changer régulièrement taies d\'oreiller et serviettes'
    ],
    treatment: [
      'Nettoyants adaptés aux peaux acnéiques',
      'Crèmes à base de peroxyde de benzoyle ou acide salicylique',
      'Soins anti-taches pour peaux noires',
      'Hydratation avec produits non gras',
      'Protection solaire quotidienne',
      'Consulter un dermatologue si acné sévère'
    ],
    products: ['Gels nettoyants', 'Crèmes anti-acné', 'Soins anti-taches', 'Hydratants matifiants', 'Protection solaire'],
    faq: [
      { question: 'Comment traiter l\'acné sur peau noire ?', answer: 'Sur peau noire, évitez les produits trop agressifs qui peuvent laisser des taches. Utilisez des soins anti-taches spécifiques et protégez-vous du soleil. Consultez notre espace conseil dermo-cosmétique.' },
      { question: 'L\'alimentation influence-t-elle l\'acné ?', answer: 'Oui, les études montrent un lien entre l\'acné et les aliments à index glycémique élevé (sucres, pain blanc). Limitez aussi les produits laitiers et privilégiez fruits et légumes.' },
      { question: 'Peut-on maquiller une peau acnéique ?', answer: 'Oui, à condition d\'utiliser des produits non comédogènes et de bien démaquiller le soir. Le maquillage minéral est souvent mieux toléré.' },
      { question: 'Quand consulter un dermatologue ?', answer: 'Consultez si l\'acné est sévère, laisse des cicatrices, ne répond pas aux traitements locaux après 2-3 mois, ou affecte votre bien-être psychologique.' }
    ],
    relatedPages: ['peau-grasse', 'peau-seche', 'taches-pigmentaires']
  },
  demangeaisons: {
    id: 'demangeaisons',
    name: 'Démangeaisons',
    icon: '🩹',
    color: 'from-purple-500 to-violet-600',
    bgColor: 'bg-purple-50',
    description: 'Les démangeaisons (prurit) peuvent avoir de nombreuses causes : allergies, peau sèche, eczéma, infections, piqûres d\'insectes. Elles peuvent être très gênantes au quotidien.',
    symptoms: [
      'Envie irrésistible de se gratter',
      'Rougeurs et irritations',
      'Peau sèche et squameuse',
      'Petits boutons ou plaques',
      'Gonflement localisé',
      'Aggravation la nuit',
      'Lésions de grattage'
    ],
    prevention: [
      'Hydrater la peau quotidiennement',
      'Utiliser des savons doux sans parfum',
      'Porter des vêtements en coton',
      'Éviter les douches trop chaudes',
      'Identifier et éviter les allergènes',
      'Couper les ongles courts',
      'Maintenir une bonne hydratation (boire de l\'eau)'
    ],
    treatment: [
      'Crèmes hydratantes et émollientes',
      'Antihistaminiques oraux',
      'Crèmes à base de cortisone (courte durée)',
      'Compresses froides pour soulager',
      'Bains à l\'avoine colloïdale',
      'Consulter si démangeaisons persistantes ou généralisées'
    ],
    products: ['Crèmes hydratantes', 'Antihistaminiques', 'Huiles apaisantes', 'Savons surgras'],
    faq: [
      { question: 'Pourquoi les démangeaisons s\'aggravent-elles la nuit ?', answer: 'La nuit, la température corporelle augmente et la peau perd plus d\'eau. De plus, on est moins distrait et plus attentif aux sensations. Hydratez bien avant le coucher.' },
      { question: 'Les démangeaisons peuvent-elles être le signe d\'une allergie ?', answer: 'Oui, les allergies sont une cause fréquente de démangeaisons. Elles peuvent être liées à des aliments, médicaments, produits cosmétiques ou tissus. Consultez si elles persistent.' },
      { question: 'Comment soulager rapidement les démangeaisons ?', answer: 'Appliquez une compresse froide, utilisez une crème apaisante ou un antihistaminique. Évitez de gratter car cela aggrave les lésions.' },
      { question: 'Quand les démangeaisons nécessitent-elles une consultation ?', answer: 'Consultez si elles sont généralisées, persistent plus de 2 semaines, s\'accompagnent de fièvre ou de lésions importantes, ou perturbent votre sommeil.' }
    ],
    relatedPages: ['peau-seche', 'acne']
  },
  sommeil: {
    id: 'sommeil',
    name: 'Troubles du sommeil',
    icon: '😴',
    color: 'from-indigo-500 to-blue-600',
    bgColor: 'bg-indigo-50',
    description: 'Les troubles du sommeil incluent l\'insomnie, les difficultés d\'endormissement et les réveils nocturnes. Un mauvais sommeil affecte la santé, l\'humeur et les performances quotidiennes.',
    symptoms: [
      'Difficulté à s\'endormir',
      'Réveils nocturnes fréquents',
      'Réveil trop précoce',
      'Sommeil non réparateur',
      'Fatigue au réveil',
      'Somnolence diurne',
      'Irritabilité et difficultés de concentration'
    ],
    prevention: [
      'Se coucher et se lever à heures fixes',
      'Éviter les écrans 1h avant le coucher',
      'Créer un environnement calme et sombre',
      'Limiter la caféine après 14h',
      'Éviter les repas lourds le soir',
      'Pratiquer une activité physique (mais pas le soir)',
      'Éviter les siestes longues',
      'Gérer le stress (relaxation, méditation)'
    ],
    treatment: [
      'Tisanes relaxantes (camomille, tilleul, verveine)',
      'Mélatonine pour réguler le cycle du sommeil',
      'Compléments à base de plantes (valériane, passiflore)',
      'Magnésium pour la relaxation',
      'Techniques de relaxation et respiration',
      'Consulter si troubles persistants (> 3 semaines)'
    ],
    products: ['Tisanes sommeil', 'Mélatonine', 'Compléments relaxants', 'Magnésium', 'Huiles essentielles (lavande)'],
    faq: [
      { question: 'Combien d\'heures de sommeil sont nécessaires ?', answer: 'Un adulte a besoin de 7 à 9 heures de sommeil par nuit. Ce besoin varie selon les individus. L\'essentiel est de se sentir reposé au réveil.' },
      { question: 'La mélatonine est-elle efficace ?', answer: 'Oui, la mélatonine aide à réguler le cycle veille-sommeil, surtout en cas de décalage horaire ou de travail posté. Elle est sans accoutumance.' },
      { question: 'Les écrans perturbent-ils vraiment le sommeil ?', answer: 'Oui, la lumière bleue des écrans bloque la sécrétion de mélatonine. Évitez les écrans au moins 1 heure avant le coucher ou utilisez un filtre lumière bleue.' },
      { question: 'Quand consulter pour des troubles du sommeil ?', answer: 'Consultez si l\'insomnie dure plus de 3 semaines, affecte votre vie quotidienne, ou s\'accompagne de ronflements importants ou d\'apnées.' }
    ],
    relatedPages: ['fatigue', 'migraines']
  },
  migraines: {
    id: 'migraines',
    name: 'Migraines',
    icon: '🤕',
    color: 'from-slate-500 to-gray-600',
    bgColor: 'bg-slate-50',
    description: 'La migraine est un mal de tête intense et pulsatile, souvent d\'un seul côté. Elle peut s\'accompagner de nausées, vomissements et sensibilité à la lumière. Elle touche plus souvent les femmes.',
    symptoms: [
      'Douleur intense pulsatile (souvent un côté)',
      'Nausées et vomissements',
      'Sensibilité à la lumière (photophobie)',
      'Sensibilité au bruit (phonophobie)',
      'Troubles visuels (aura)',
      'Durée de 4 à 72 heures',
      'Aggravation par l\'activité physique'
    ],
    prevention: [
      'Identifier et éviter les facteurs déclenchants',
      'Dormir suffisamment et régulièrement',
      'Manger à heures fixes, ne pas sauter de repas',
      'Limiter alcool et caféine',
      'Gérer le stress',
      'Éviter les lumières vives et écrans prolongés',
      'Pratiquer une activité physique régulière',
      'Tenir un journal des migraines'
    ],
    treatment: [
      'Repos dans un endroit calme et sombre',
      'Antalgiques (paracétamol, ibuprofène) dès les premiers signes',
      'Application de froid sur le front',
      'Hydratation',
      'Traitements spécifiques (triptans) sur prescription',
      'Consulter si migraines fréquentes (> 4/mois)'
    ],
    products: ['Paracétamol', 'Ibuprofène', 'Patchs anti-douleur', 'Baume du tigre', 'Huile de menthe poivrée'],
    faq: [
      { question: 'Quelle est la différence entre migraine et mal de tête ?', answer: 'La migraine est plus intense, souvent d\'un seul côté, pulsatile, et s\'accompagne souvent de nausées et sensibilité à la lumière. Un mal de tête ordinaire est généralement bilatéral et moins invalidant.' },
      { question: 'Quels sont les facteurs déclenchants de la migraine ?', answer: 'Les déclenchants courants sont le stress, le manque de sommeil, certains aliments (chocolat, fromage, alcool), les règles, les changements météo et les écrans.' },
      { question: 'La migraine peut-elle être grave ?', answer: 'La migraine est bénigne mais invalidante. Consultez en urgence si le mal de tête est soudain et très intense, accompagné de fièvre, raideur de nuque ou confusion.' },
      { question: 'Comment traiter une crise de migraine ?', answer: 'Prenez un antalgique dès les premiers signes, reposez-vous dans une pièce sombre et calme, appliquez du froid sur le front et hydratez-vous bien.' }
    ],
    relatedPages: ['sommeil', 'fatigue', 'hta']
  },
  // Nouvelles pages SEO
  'peau-seche': {
    id: 'peau-seche',
    name: 'Peau sèche',
    icon: '💧',
    color: 'from-cyan-500 to-blue-600',
    bgColor: 'bg-cyan-50',
    description: 'La peau sèche (xérose) se caractérise par un manque d\'hydratation et de lipides. Elle peut être inconfortable, tiraillée et sujette aux démangeaisons. Les peaux noires et métissées sont particulièrement concernées.',
    symptoms: [
      'Tiraillements et inconfort',
      'Aspect terne et rugueux',
      'Desquamation (petites peaux mortes)',
      'Démangeaisons',
      'Fissures sur les mains et pieds',
      'Peau qui pèle',
      'Sensibilité accrue'
    ],
    prevention: [
      'Hydrater matin et soir avec une crème adaptée',
      'Utiliser des savons surgras ou sans savon',
      'Éviter les douches trop chaudes et prolongées',
      'Boire au moins 1,5L d\'eau par jour',
      'Protéger la peau du soleil et du vent',
      'Utiliser un humidificateur en saison sèche',
      'Éviter les produits avec alcool'
    ],
    treatment: [
      'Crèmes et baumes hydratants riches',
      'Huiles végétales (karité, argan, coco)',
      'Émollients pour peaux très sèches',
      'Sérum hydratant à l\'acide hyaluronique',
      'Masques nourrissants hebdomadaires',
      'Consulter si sécheresse sévère ou eczéma'
    ],
    products: ['Crèmes hydratantes', 'Huile de karité', 'Savons surgras', 'Baumes réparateurs', 'Sérums hydratants'],
    faq: [
      { question: 'Pourquoi ma peau est-elle sèche ?', answer: 'La peau sèche peut être due au climat (harmattan, climatisation), à l\'âge, aux douches chaudes fréquentes, à l\'utilisation de savons agressifs ou à certaines maladies (eczéma, psoriasis).' },
      { question: 'Quelle est la meilleure crème pour peau sèche ?', answer: 'Choisissez une crème riche en agents hydratants (glycérine, acide hyaluronique) et en lipides (beurre de karité, huiles végétales). Évitez les produits parfumés.' },
      { question: 'Comment hydrater une peau très sèche ?', answer: 'Appliquez une crème hydratante sur peau humide après la douche, utilisez un savon surgras, et appliquez un baume nourrissant le soir. Buvez suffisamment d\'eau.' },
      { question: 'La peau sèche est-elle héréditaire ?', answer: 'Oui, la tendance à avoir la peau sèche peut être héréditaire. Certaines personnes produisent naturellement moins de sébum. Mais les soins adaptés permettent de compenser.' }
    ],
    relatedPages: ['demangeaisons', 'acne', 'peau-grasse']
  },
  'peau-grasse': {
    id: 'peau-grasse',
    name: 'Peau grasse',
    icon: '✨',
    color: 'from-lime-500 to-green-600',
    bgColor: 'bg-lime-50',
    description: 'La peau grasse se caractérise par une production excessive de sébum. Elle est brillante, a des pores dilatés et est sujette aux imperfections. Elle nécessite des soins spécifiques pour réguler le sébum sans l\'agresser.',
    symptoms: [
      'Brillance excessive surtout zone T',
      'Pores dilatés et visibles',
      'Points noirs fréquents',
      'Tendance acnéique',
      'Maquillage qui ne tient pas',
      'Teint terne',
      'Peau épaisse'
    ],
    prevention: [
      'Nettoyer la peau matin et soir sans l\'agresser',
      'Utiliser des produits matifiants',
      'Hydrater avec des textures légères',
      'Éviter les produits comédogènes',
      'Ne pas toucher son visage',
      'Limiter les aliments gras et sucrés',
      'Utiliser des papiers matifiants en journée'
    ],
    treatment: [
      'Nettoyants doux sans savon',
      'Toniques astringents',
      'Crèmes hydratantes légères',
      'Masques à l\'argile (1-2x/semaine)',
      'Sérums régulateurs de sébum',
      'Protection solaire non comédogène'
    ],
    products: ['Gels nettoyants', 'Crèmes matifiantes', 'Masques à l\'argile', 'Papiers matifiants', 'Sérums purifiants'],
    faq: [
      { question: 'Faut-il hydrater une peau grasse ?', answer: 'Oui, absolument ! Une peau grasse déshydratée produit encore plus de sébum pour compenser. Utilisez une crème légère, non comédogène, adaptée aux peaux grasses.' },
      { question: 'Comment réduire les pores dilatés ?', answer: 'Nettoyez bien votre peau, utilisez des soins à l\'acide salicylique, faites des masques à l\'argile régulièrement et appliquez un sérum resserrant les pores.' },
      { question: 'Le soleil est-il bon pour la peau grasse ?', answer: 'Non, c\'est un mythe ! Le soleil épaissit la peau et provoque un effet rebond avec plus de sébum et de boutons. Protégez-vous avec une crème solaire non comédogène.' },
      { question: 'Quels aliments éviter en cas de peau grasse ?', answer: 'Limitez les aliments à index glycémique élevé (sucres, pain blanc), les produits laitiers et les aliments très gras. Privilégiez les fruits, légumes et oméga-3.' }
    ],
    relatedPages: ['acne', 'peau-seche', 'taches-pigmentaires']
  },
  'taches-pigmentaires': {
    id: 'taches-pigmentaires',
    name: 'Taches pigmentaires',
    icon: '🎨',
    color: 'from-amber-500 to-yellow-600',
    bgColor: 'bg-amber-50',
    description: 'Les taches pigmentaires (hyperpigmentation) sont des zones de peau plus foncées, fréquentes sur les peaux noires et métissées. Elles peuvent être causées par le soleil, l\'acné, les blessures ou les changements hormonaux.',
    symptoms: [
      'Taches brunes ou noires localisées',
      'Teint irrégulier',
      'Marques post-acné',
      'Masque de grossesse (mélasma)',
      'Taches de vieillesse',
      'Cicatrices pigmentées'
    ],
    prevention: [
      'Protection solaire quotidienne SPF 30 minimum',
      'Ne pas percer les boutons',
      'Éviter les produits irritants',
      'Traiter rapidement les inflammations cutanées',
      'Utiliser des produits adaptés aux peaux pigmentées',
      'Éviter les dépigmentants dangereux'
    ],
    treatment: [
      'Crèmes éclaircissantes (vitamine C, arbutine, acide kojique)',
      'Sérums anti-taches',
      'Protection solaire haute',
      'Exfoliation douce régulière',
      'Soins dépigmentants dermatologiques',
      'Consulter si taches qui changent d\'aspect'
    ],
    products: ['Sérums vitamine C', 'Crèmes anti-taches', 'Protection solaire', 'Gommages doux', 'Soins unifiants'],
    faq: [
      { question: 'Comment éviter les taches après l\'acné ?', answer: 'Ne percez jamais vos boutons, utilisez une protection solaire quotidienne, et traitez l\'acné rapidement. Appliquez un soin anti-taches dès la guérison.' },
      { question: 'Les taches pigmentaires peuvent-elles disparaître ?', answer: 'Oui, avec patience et les bons soins. Les taches superficielles s\'estompent en quelques mois. Les taches profondes sont plus longues à traiter. La protection solaire est essentielle.' },
      { question: 'Les crèmes dépigmentantes sont-elles dangereuses ?', answer: 'Attention aux produits non contrôlés ! Évitez les crèmes à l\'hydroquinone forte ou aux corticoïdes. Privilégiez les soins dermatologiques avec vitamine C, arbutine ou acide kojique.' },
      { question: 'Quel SPF pour les peaux noires ?', answer: 'Les peaux noires ont aussi besoin de protection ! Utilisez un SPF 30 minimum, surtout si vous traitez des taches. Les formules invisibles existent pour éviter les traces blanches.' }
    ],
    relatedPages: ['acne', 'peau-grasse', 'peau-seche']
  },
  'fatigue': {
    id: 'fatigue',
    name: 'Fatigue',
    icon: '😫',
    color: 'from-gray-500 to-slate-600',
    bgColor: 'bg-gray-50',
    description: 'La fatigue est une sensation d\'épuisement physique ou mental. Elle peut être passagère ou chronique. Quand elle persiste malgré le repos, elle peut révéler un problème de santé sous-jacent.',
    symptoms: [
      'Épuisement physique',
      'Manque d\'énergie au réveil',
      'Difficultés de concentration',
      'Somnolence diurne',
      'Irritabilité',
      'Baisse de motivation',
      'Faiblesse musculaire'
    ],
    prevention: [
      'Dormir 7-9 heures par nuit',
      'Maintenir des horaires réguliers',
      'Pratiquer une activité physique modérée',
      'Alimentation équilibrée',
      'Gérer le stress',
      'Limiter alcool et caféine',
      'Faire des pauses régulières'
    ],
    treatment: [
      'Vitamines B et C',
      'Fer (si carence)',
      'Magnésium',
      'Compléments adaptogènes (ginseng, rhodiola)',
      'Amélioration du sommeil',
      'Bilan médical si fatigue persistante'
    ],
    products: ['Multivitamines', 'Fer', 'Magnésium', 'Vitamine C', 'Compléments énergétiques'],
    faq: [
      { question: 'Quand la fatigue est-elle anormale ?', answer: 'Consultez si la fatigue persiste plus de 2 semaines malgré le repos, s\'accompagne de fièvre, perte de poids ou autres symptômes, ou affecte votre vie quotidienne.' },
      { question: 'Quelles vitamines prendre contre la fatigue ?', answer: 'Les vitamines B (B1, B6, B12), la vitamine C, le fer et le magnésium sont les plus efficaces. Un bilan sanguin peut identifier une carence spécifique.' },
      { question: 'Le café aide-t-il vraiment contre la fatigue ?', answer: 'Le café donne un coup de boost temporaire mais n\'agit pas sur la vraie fatigue. Trop de caféine peut perturber le sommeil et aggraver la fatigue.' },
      { question: 'Comment avoir plus d\'énergie naturellement ?', answer: 'Dormez suffisamment, mangez équilibré, bougez régulièrement, hydratez-vous bien et gérez votre stress. Les compléments peuvent aider en période difficile.' }
    ],
    relatedPages: ['sommeil', 'carence-vitamines', 'diabete']
  },
  'douleurs-articulaires': {
    id: 'douleurs-articulaires',
    name: 'Douleurs articulaires',
    icon: '🦴',
    color: 'from-stone-500 to-neutral-600',
    bgColor: 'bg-stone-50',
    description: 'Les douleurs articulaires touchent les genoux, hanches, mains, dos et autres articulations. Elles peuvent être dues à l\'arthrose, l\'arthrite, des traumatismes ou l\'usure naturelle.',
    symptoms: [
      'Douleurs au niveau des articulations',
      'Raideur matinale',
      'Gonflement articulaire',
      'Craquements',
      'Diminution de la mobilité',
      'Douleurs au mouvement',
      'Déformation progressive'
    ],
    prevention: [
      'Maintenir un poids santé',
      'Pratiquer une activité physique adaptée',
      'Éviter les mouvements répétitifs',
      'S\'échauffer avant l\'effort',
      'Adopter de bonnes postures',
      'Consommer des oméga-3',
      'S\'hydrater suffisamment'
    ],
    treatment: [
      'Antalgiques (paracétamol)',
      'Anti-inflammatoires (courte durée)',
      'Chondroprotecteurs (glucosamine, chondroïtine)',
      'Kinésithérapie',
      'Application de chaud ou froid',
      'Infiltrations (si nécessaire)'
    ],
    products: ['Paracétamol', 'Glucosamine', 'Chondroïtine', 'Collagène', 'Patchs chauffants', 'Gels anti-douleur'],
    faq: [
      { question: 'Quelle est la différence entre arthrose et arthrite ?', answer: 'L\'arthrose est une usure du cartilage (mécanique). L\'arthrite est une inflammation (souvent auto-immune). L\'arthrose touche surtout les personnes âgées, l\'arthrite peut survenir à tout âge.' },
      { question: 'La glucosamine est-elle efficace ?', answer: 'Les études montrent que la glucosamine et la chondroïtine peuvent ralentir l\'usure du cartilage et réduire la douleur. Elles sont plus efficaces en traitement de fond, sur plusieurs mois.' },
      { question: 'Faut-il appliquer du chaud ou du froid ?', answer: 'Le froid est recommandé en cas d\'inflammation aiguë (gonflement, rougeur). Le chaud soulage les raideurs chroniques et contractures. Alternez selon vos symptômes.' },
      { question: 'Le sport est-il déconseillé en cas de douleurs articulaires ?', answer: 'Au contraire ! Une activité physique adaptée (natation, vélo, marche) renforce les muscles qui soutiennent les articulations. Évitez juste les sports à impact comme la course.' }
    ],
    relatedPages: ['fatigue', 'carence-vitamines']
  },
  'carence-vitamines': {
    id: 'carence-vitamines',
    name: 'Carence en vitamines',
    icon: '💊',
    color: 'from-emerald-500 to-teal-600',
    bgColor: 'bg-emerald-50',
    description: 'Les carences en vitamines et minéraux sont fréquentes et peuvent causer fatigue, troubles de l\'humeur, problèmes de peau et affaiblissement immunitaire. Une alimentation variée et des compléments adaptés permettent de les corriger.',
    symptoms: [
      'Fatigue persistante',
      'Pâleur (anémie)',
      'Chute de cheveux',
      'Ongles cassants',
      'Peau sèche',
      'Crampes musculaires',
      'Infections fréquentes',
      'Troubles de l\'humeur'
    ],
    prevention: [
      'Alimentation variée et équilibrée',
      'Consommer des fruits et légumes frais',
      'Inclure des protéines (viande, poisson, œufs)',
      'Exposition modérée au soleil (vitamine D)',
      'Limiter les aliments transformés',
      'Bilan sanguin régulier'
    ],
    treatment: [
      'Compléments vitaminiques adaptés',
      'Fer en cas d\'anémie',
      'Vitamine D (soleil, compléments)',
      'Vitamines B pour l\'énergie',
      'Magnésium pour les muscles',
      'Zinc pour l\'immunité'
    ],
    products: ['Multivitamines', 'Fer', 'Vitamine D', 'Vitamines B', 'Magnésium', 'Zinc'],
    faq: [
      { question: 'Comment savoir si j\'ai une carence ?', answer: 'Un bilan sanguin permet de détecter les carences. Les signes courants sont la fatigue, pâleur, chute de cheveux, ongles cassants et infections fréquentes.' },
      { question: 'Quelles sont les carences les plus fréquentes ?', answer: 'Le fer (surtout chez les femmes), la vitamine D (manque de soleil), les vitamines B12 (régimes végétariens), le magnésium et le zinc sont les carences les plus courantes.' },
      { question: 'Peut-on prendre trop de vitamines ?', answer: 'Oui, un excès de certaines vitamines (A, D, E) peut être toxique. Respectez les doses recommandées et préférez les multivitamines équilibrés aux méga-doses.' },
      { question: 'Les compléments alimentaires sont-ils vraiment utiles ?', answer: 'Ils sont utiles pour combler des carences spécifiques ou en période de besoins accrus (grossesse, convalescence, sport intensif). Ils ne remplacent pas une alimentation équilibrée.' }
    ],
    relatedPages: ['fatigue', 'diabete', 'douleurs-articulaires']
  }
};

const PharmacyPathologyPage = ({ pathologyId, onBack, onNavigate }) => {
  const pathology = pathologiesData[pathologyId];
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  
  // Inject FAQ Schema for SEO
  useEffect(() => {
    if (pathology?.faq) {
      const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": pathology.faq.map(item => ({
          "@type": "Question",
          "name": item.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": item.answer
          }
        }))
      };
      
      // Remove existing FAQ schema
      const existingScript = document.getElementById('faq-schema');
      if (existingScript) existingScript.remove();
      
      // Add new FAQ schema
      const script = document.createElement('script');
      script.id = 'faq-schema';
      script.type = 'application/ld+json';
      script.text = JSON.stringify(faqSchema);
      document.head.appendChild(script);
      
      // Update page title for SEO
      document.title = `${pathology.name} - Conseils et traitement | Pharmacie Saint Ange Des Amis`;
      
      return () => {
        const scriptToRemove = document.getElementById('faq-schema');
        if (scriptToRemove) scriptToRemove.remove();
      };
    }
  }, [pathology]);
  
  if (!pathology) {
    return (
      <div className="min-h-screen bg-[#FDF8F3] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#8B7355]">Pathologie non trouvée</p>
          <Button onClick={onBack} className="mt-4">Retour</Button>
        </div>
      </div>
    );
  }

  const logoUrl = 'https://customer-assets.emergentagent.com/job_skincare-check-1/artifacts/6cemh04i_LOGO%20DEF.jpg';

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FDF8F3] to-white">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-md border-b border-[#E8DDD4] sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={onBack}
              className="text-[#8B7355]"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <img 
              src={logoUrl} 
              alt="Logo" 
              className="h-8 w-auto object-contain cursor-pointer"
              onClick={() => onNavigate('home')}
            />
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Hero */}
        <div className={`bg-gradient-to-r ${pathology.color} rounded-3xl p-8 text-white mb-8`}>
          <div className="flex items-center gap-4 mb-4">
            <span className="text-5xl">{pathology.icon}</span>
            <div>
              <h1 className="text-3xl font-bold">{pathology.name}</h1>
              <p className="text-white/80">Informations et conseils</p>
            </div>
          </div>
          <p className="text-white/90 leading-relaxed">
            {pathology.description}
          </p>
        </div>

        {/* Warning Banner */}
        <Card className="bg-amber-50 border-amber-200 mb-8">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0" />
            <div>
              <p className="font-semibold text-amber-800">Important</p>
              <p className="text-amber-700 text-sm">
                Ces informations sont données à titre indicatif et ne remplacent pas une consultation médicale. 
                <strong> Demandez toujours conseil à votre pharmacien ou consultez un médecin.</strong>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Symptoms */}
        <Card className={`mb-6 border-0 shadow-lg overflow-hidden`}>
          <div className={`h-2 bg-gradient-to-r ${pathology.color}`} />
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-10 h-10 ${pathology.bgColor} rounded-xl flex items-center justify-center`}>
                <Stethoscope className="w-5 h-5 text-[#5D4E37]" />
              </div>
              <h2 className="text-xl font-bold text-[#5D4E37]">Symptômes</h2>
            </div>
            <ul className="space-y-2">
              {pathology.symptoms.map((symptom, index) => (
                <li key={index} className="flex items-start gap-2 text-[#5D4E37]">
                  <span className="text-[#C45C26] mt-1">•</span>
                  {symptom}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Prevention */}
        <Card className="mb-6 border-0 shadow-lg overflow-hidden">
          <div className="h-2 bg-gradient-to-r from-green-500 to-emerald-500" />
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                <Shield className="w-5 h-5 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-[#5D4E37]">Prévention</h2>
            </div>
            <ul className="space-y-2">
              {pathology.prevention.map((item, index) => (
                <li key={index} className="flex items-start gap-2 text-[#5D4E37]">
                  <span className="text-green-600 mt-1">✓</span>
                  {item}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Treatment */}
        <Card className="mb-6 border-0 shadow-lg overflow-hidden">
          <div className="h-2 bg-gradient-to-r from-blue-500 to-cyan-500" />
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <span className="text-xl">💊</span>
              </div>
              <h2 className="text-xl font-bold text-[#5D4E37]">Traitement</h2>
            </div>
            <ul className="space-y-2">
              {pathology.treatment.map((item, index) => (
                <li key={index} className="flex items-start gap-2 text-[#5D4E37]">
                  <span className="text-blue-600 mt-1">→</span>
                  {item}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Products */}
        <Card className="mb-8 border-0 shadow-lg bg-gradient-to-br from-[#C45C26]/10 to-[#C45C26]/5">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#C45C26]/20 rounded-xl flex items-center justify-center">
                <ShoppingBag className="w-5 h-5 text-[#C45C26]" />
              </div>
              <h2 className="text-xl font-bold text-[#5D4E37]">Produits disponibles en pharmacie</h2>
            </div>
            <div className="flex flex-wrap gap-2 mb-4">
              {pathology.products.map((product, index) => (
                <Badge 
                  key={index} 
                  className="bg-white text-[#5D4E37] border border-[#E8DDD4] px-3 py-1"
                >
                  {product}
                </Badge>
              ))}
            </div>
            <Button
              className="w-full bg-[#C45C26] hover:bg-[#A84B1F]"
              onClick={() => onNavigate('catalog', { search: pathology.name })}
            >
              Voir les produits disponibles
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>

        {/* FAQ Section */}
        {pathology.faq && pathology.faq.length > 0 && (
          <Card className="mb-8 border-0 shadow-lg overflow-hidden">
            <div className="h-2 bg-gradient-to-r from-[#C45C26] to-[#A84B1F]" />
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-[#C45C26]/10 rounded-xl flex items-center justify-center">
                  <HelpCircle className="w-5 h-5 text-[#C45C26]" />
                </div>
                <h2 className="text-xl font-bold text-[#5D4E37]">Questions fréquentes</h2>
              </div>
              <div className="space-y-3">
                {pathology.faq.map((item, index) => (
                  <div 
                    key={index}
                    className="border border-[#E8DDD4] rounded-xl overflow-hidden"
                  >
                    <button
                      onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                      className="w-full p-4 text-left flex items-center justify-between bg-[#FDF8F3] hover:bg-[#F5EDE4] transition-colors"
                    >
                      <span className="font-medium text-[#5D4E37] pr-4">{item.question}</span>
                      <ChevronDown className={`w-5 h-5 text-[#C45C26] flex-shrink-0 transition-transform ${openFaqIndex === index ? 'rotate-180' : ''}`} />
                    </button>
                    {openFaqIndex === index && (
                      <div className="p-4 bg-white border-t border-[#E8DDD4]">
                        <p className="text-[#5D4E37] leading-relaxed">{item.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Related Pages - Internal Links */}
        {pathology.relatedPages && pathology.relatedPages.length > 0 && (
          <Card className="mb-8 border-0 shadow-lg bg-[#FDF8F3]">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-[#2E7D32]/10 rounded-xl flex items-center justify-center">
                  <Link2 className="w-5 h-5 text-[#2E7D32]" />
                </div>
                <h2 className="text-xl font-bold text-[#5D4E37]">Articles connexes</h2>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {pathology.relatedPages.map((pageId) => {
                  const relatedPage = pathologiesData[pageId];
                  if (!relatedPage) return null;
                  return (
                    <button
                      key={pageId}
                      onClick={() => onNavigate('pathology', { id: pageId })}
                      className="flex items-center gap-3 p-3 bg-white rounded-xl border border-[#E8DDD4] hover:border-[#C45C26] hover:shadow-md transition-all text-left"
                    >
                      <span className="text-2xl">{relatedPage.icon}</span>
                      <div>
                        <p className="font-medium text-[#5D4E37]">{relatedPage.name}</p>
                        <p className="text-xs text-[#8B7355]">En savoir plus</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-[#C45C26] ml-auto" />
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* CTA */}
        <Card className="bg-gradient-to-r from-[#2E7D32] to-[#1B5E20] border-0 text-white">
          <CardContent className="p-6 text-center">
            <Phone className="w-10 h-10 mx-auto mb-3" />
            <h3 className="text-xl font-bold mb-2">Besoin de conseils personnalisés ?</h3>
            <p className="text-white/90 mb-4">
              Nos pharmaciens sont à votre disposition pour vous accompagner
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                variant="secondary"
                className="bg-white text-[#2E7D32] hover:bg-green-50"
                asChild
              >
                <a href="tel:0778415277">
                  <Phone className="w-4 h-4 mr-2" />
                  Appeler : 07 78 41 52 77
                </a>
              </Button>
              <Button
                variant="outline"
                className="border-white text-white hover:bg-white/20"
                onClick={() => onNavigate('contact')}
              >
                Nous contacter
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <footer className="bg-[#5D4E37] text-white py-6 mt-8">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-[#D4C4B0] text-sm">
            ⚠️ Les informations de cette page ne remplacent pas l'avis d'un professionnel de santé.
          </p>
          <p className="text-[#D4C4B0] text-xs mt-2">
            © 2026 Pharmacie Saint Ange Des Amis
          </p>
        </div>
      </footer>
    </div>
  );
};

// Export the pathologies data for use in other components
export { pathologiesData };
export default PharmacyPathologyPage;
